<template>
  <div>
    <b-table
      id="alerts-table"
      class="table-with-actions-min-height"
      responsive
      small
      show-empty
      primary-key="id"
      :items="alerts"
      :fields="tableFields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="filter"
      @filtered="onFiltered"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
    >
      <template #table-colgroup="scope">
        <col
          v-for="field in scope.fields"
          :key="field.key"
          :style="{ width: colWidth(field.key), minWidth: colMinWidth(field.key) }"
        >
      </template>
      <template #cell(threatInfo)="{ item, toggleDetails }">
        <b-row>
          <b-col>{{ item.events.join(', ') }}</b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-button
              variant="flat-primary"
              class="p-0"
              @click="showRowDetail({ item, toggleDetails }, 'alerts')"
            >
              {{ item.count }} record(s)
            </b-button>
          </b-col>
        </b-row>
      </template>
      <template #cell(firstSeen)="row">
        {{ formatDate(row.value) }}
      </template>
      <template #cell(lastSeen)="row">
        {{ formatDate(row.value) }}
      </template>
      <template #cell(severity)="row">
        <b-badge pill :variant="alertStatusVariant(row.value)">
          {{ alertStatusText(row.value)}}
        </b-badge>
      </template>
      <template #cell(actions)="{ item, toggleDetails }">
        <div class="d-flex align-items-center">
          <b-icon
            icon="stars"
            class="cursor-pointer text-primary"
            title="AI Suggestions"
            @click="showRowDetail({ item, toggleDetails }, 'ai')"
          />
          <b-icon
            icon="box-arrow-up-right"
            class="cursor-pointer text-primary ml-2"
            title="ATT&CK Navigator"
            @click="openNavigator(item)"
          />
          <b-icon
            icon="eye"
            class="cursor-pointer text-primary ml-2"
            title="Show Evidence"
            @click="showRowDetail({ item, toggleDetails }, 'events')"
          />
        </div>
      </template>
      <template #row-details="{ item, toggleDetails }">
        <div>
          <ThreatAlertsTable
            v-if="detailMode[item.id] === 'alerts'"
            :alerts="item.alerts"
            @close="() => closeDetail({ item, toggleDetails })"
          />
          <ThreatEventsTable
            v-if="detailMode[item.id] === 'events'"
            :item="item"
            @close="() => closeDetail({ item, toggleDetails })"
          />
          <AlertSuggestionsContent
            v-else-if="detailMode[item.id] === 'ai'"
            :alertAggkey="item.alerts[0].id"
            @close="() => closeDetail({ item, toggleDetails })"
          />
        </div>
      </template>
    </b-table>
    <b-pagination
      v-model="currentPage"
      :total-rows="paginationTotalRows"
      :per-page="perPage"
      first-number
      last-number
      prev-class="prev-item"
      next-class="next-item"
      align="right"
      class="mt-1 mr-1"
      ariaControls="alerts-table"
    ></b-pagination>
  </div>
</template>

<script>
import moment from 'moment';

import AlertSuggestionsContent from '@/xvisor/components/app/AlertSuggestionsContent.vue';
import ThreatAlertsTable from '@/xvisor/components/security/ThreatAlertsTable.vue';
import ThreatEventsTable from '@/xvisor/components/security/ThreatEventsTable.vue';
import alertSeverities from '@/xvisor/constants/alertSeverities';
import colorPalette from '@/xvisor/constants/colorPalette';
import momentTimeFormat from '@/xvisor/constants/momentTimeFormat';

export default {
  components: {
    ThreatAlertsTable,
    ThreatEventsTable,
    AlertSuggestionsContent,
  },
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    filter: {
      type: String,
      default: '',
    },
    perPage: {
      type: Number,
      required: true,
    },
    items: {
      type: Array,
      default: null,
    },
    navigatorConfig: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      alerts: [],
      resolved: false,
      currentPage: 1,
      filteredLength: null,
      sortBy: 'severity',
      sortDesc: false,
      tableFields: [
        { key: 'ip', label: 'IP', sortable: true },
        { key: 'threatInfo', label: 'Threat Info' },
        { key: 'firstSeen', label: 'First Seen', sortable: true },
        { key: 'lastSeen', label: 'Last Seen', sortable: true },
        { key: 'severity', label: 'Severity', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      colorPalette,
      detailMode: {},
    };
  },
  computed: {
    paginationTotalRows() {
      return this.filteredLength !== null ? this.filteredLength : this.alerts.length;
    },
    showEventsLink() {
      return this.displayAppAlerts;
    },
  },
  watch: {
    filter() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
    timeRange() {
      if (!this.items) this.httpGet();
      this.currentPage = 1;
    },
    items: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val)) {
          this.alerts = val;
          this.$emit('total-rows', this.alerts.length);
        }
      },
    },
  },
  mounted() {
    if (!this.items) this.httpGet();
  },
  methods: {
    alertStatusVariant(status) {
      if (status === alertSeverities.critical) return 'danger';
      if (status === alertSeverities.warning) return 'warning';
      return 'success';
    },
    alertStatusText(status) {
      if (status === alertSeverities.critical) return 'Critical';
      if (status === alertSeverities.warning) return 'Warning';
      return 'Notice';
    },
    httpGet() {
      this.$http
        .get('/alerts/ip/threats.json', {
          params: {
            start: this.timeRange.start.toISOString(),
            end: this.timeRange.end.toISOString(),
          },
        })
        .then((response) => {
          this.alerts = response.data;
          this.$emit('total-rows', Array.isArray(this.alerts) ? this.alerts.length : 0);
        });
    },
    onFiltered(filteredItems) {
      this.$emit('filtered-length', filteredItems.length);
      this.filteredLength = filteredItems.length;
      this.currentPage = 1;
    },
    formatDate(dateStr) {
      return moment(dateStr).format(momentTimeFormat.dateTime);
    },
    colWidth(fieldKey) {
      switch (fieldKey) {
        case 'firstSeen': return '10%';
        case 'lastSeen': return '10%';
        case 'threatInfo': return '50%';
        case 'severity': return '5%';
        case 'ip': return '15%';
        case 'actions': return '10%';
        default: return '180px';
      }
    },
    colMinWidth(fieldKey) {
      switch (fieldKey) {
        case 'firstSeen': return '100px';
        case 'lastSeen': return '100px';
        case 'threatInfo': return '500px';
        case 'severity': return '50px';
        case 'ip': return '150px';
        case 'actions': return '100px';
        default: return '80px';
      }
    },
    showRowDetail({ item, toggleDetails }, mode) {
      const isSameMode = this.detailMode[item.id] === mode;
      const isOpen = this.detailMode[item.id] !== undefined;
      if (isSameMode) {
        // If clicking the same button again, toggle (hide)
        this.$delete(this.detailMode, item.id);
        toggleDetails();
      } else {
        this.$set(this.detailMode, item.id, mode);
        // only open if not already opened
        if (!isOpen) {
          toggleDetails();
        }
      }
    },
    closeDetail({ item, toggleDetails }) {
      this.$delete(this.detailMode, item.id);
      toggleDetails();
    },
    calculateNavigatorLink(item) {
      const start = this.timeRange.start.toISOString();
      const end = this.timeRange.end.toISOString();
      const cfg = this.navigatorConfig;
      const id = item?.id;
      if (!id) return null;
      const layerUrl = `${cfg.networkLayerBase}${id}.json?start=${start}&end=${end}`;
      return `${cfg.baseUrl}${encodeURIComponent(layerUrl)}`;
    },
    openNavigator(item) {
      const url = this.calculateNavigatorLink(item);
      if (!url) return;
      window.open(url, '_blank');
    },
  },
};
</script>
